import {
  FilePresent,
  HighlightOff,
  InsertDriveFile,
  MoreVert,
  Tune,
} from '@mui/icons-material';
import {
  Divider,
  MenuItem,
  Stack,
  useTheme,
  useMediaQuery,
  Button,
  Badge,
  Typography,
  Box,
  Grid,
} from '@mui/material';
import {
  GenSearch,
  DateRange,
  GenContextualMenu,
  GenSnackBar,
} from '@pinnacleautomotive/clarity-components';
import React, { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { ProcessStep } from '../../../common/enums/enums';
import DownloadCsv from '../../../common/helpers/DownloadCsv';
import DownloadPdf from '../../../common/helpers/DownloadPdf';
import {
  exportAllOrdersDataCSV,
  exportAllOrdersDataPDF,
} from '../../../redux/actions/workQueue/allOrdersActions';
import { useGetVendorsQuery } from '../../../redux/rtk/vendors.services';
import {
  currentUserCompanyId,
  selectUserDepartments,
} from '../../../redux/selectors/userDataSelectors';
import {
  selectAllOrdersTotalWO,
  selectIsLoading,
} from '../../../redux/selectors/workQueue/allOrdersSelectors';
import {
  ORDERS_VIEW,
  useViewOrdersOrVehicle,
} from '../ViewOrdersOrVehicle.provider';
import FilterWoCount from './FilterWoCount';
import MultiSelectFilter from './MultiSelectFilter';

const VALID_STEPS = Object.values(ProcessStep).filter(
  step => step !== ProcessStep.RESUME && step !== ProcessStep.TRANSFERRED
);

const activeFilters = (filters, isDoneTab) => {
  const globalFilters =
    !!filters?.selectedDepartments?.length ||
    !!filters?.selectedVendors?.length ||
    !!filters?.dateRange?.startingDate ||
    !!filters?.dateRange?.endDate;

  if (isDoneTab) return globalFilters;

  return globalFilters || !!filters?.selectedSteps?.length;
};

const showDivider = () => <Divider orientation="vertical" flexItem />;
const showFilterWoCount = (totalWorkOrders, isLoading, tab, isLargeScreen) => {
  return (
    <>
      <FilterWoCount
        totalWorkOrders={totalWorkOrders}
        isLoading={isLoading}
        tab={tab}
      />
      {isLargeScreen && showDivider()}
    </>
  );
};
const getTabName = isDoneTab => (isDoneTab ? 'DoneOrders' : 'AllOrders');
const getPopMenu = (view, popMenu) => {
  if (view !== ORDERS_VIEW) {
    popMenu = popMenu.splice(0, 2);
  }
  return popMenu;
};

const Filters = ({
  onFilter,
  currentFilters,
  handleClear,
  hideFilterBySteps,
  hideFilterByDepartments,
  dateRangeFlexGrow = 0,
  startPlaceholder,
  endPlaceholder,
  isDoneTab,
  children,
}) => {
  const theme = useTheme();
  const { t } = useTranslation('orders');
  const { view } = useViewOrdersOrVehicle();
  const dispatch = useDispatch();
  const companyId = useSelector(currentUserCompanyId);
  const { data: vendors } = useGetVendorsQuery(companyId);
  const departments = useSelector(selectUserDepartments);
  const totalWorkOrders = useSelector(selectAllOrdersTotalWO);
  const isLoading = useSelector(selectIsLoading);

  const largeScreen = useMediaQuery(theme.breakpoints.up('lg'));
  const mediumScreenDown = useMediaQuery(theme.breakpoints.down('md'));
  const smallScreen = useMediaQuery(theme.breakpoints.down('sm'));

  const [query, setQuery] = useState('');
  const [filterDisplay, setFilterDisplay] = useState(false);
  const [openGenSnack, setopenGenSnack] = useState(false);
  const [openGenSnackError, setopenGenSnackError] = useState(false);
  const tab = getTabName(isDoneTab);

  const setSearchQuery = newSearch => {
    if (query === newSearch) return;
    setQuery(newSearch);
    onFilter({
      ...currentFilters,
      searchQuery: newSearch,
    });
  };

  const setDateRangeWrapper = fn => {
    const newData = fn(currentFilters.dateRange);

    if (newData.endDate?.endDate === '') return;
    if (
      newData.startingDate === currentFilters.dateRange.startingDate &&
      newData.endDate === currentFilters.dateRange.endDate
    )
      return;
    onFilter({
      ...currentFilters,
      dateRange: newData,
    });
  };

  const filterDisplayHandler = () => {
    setFilterDisplay(state => !state);
  };

  const exportPdf = () => {
    setopenGenSnack(true);

    dispatch(
      exportAllOrdersDataPDF({
        filters: currentFilters,
        companyId,
        isDoneTab,
      })
    ).then(({ payload: pdfString }) => {
      if (pdfString) {
        DownloadPdf(pdfString, 'Orders');
        setopenGenSnack(false);
      } else {
        setopenGenSnackError(true);
      }
    });
  };

  const exportCsv = () => {
    setopenGenSnack(true);

    dispatch(
      exportAllOrdersDataCSV({
        filters: currentFilters,
        companyId,
        isDoneTab,
      })
    ).then(({ payload: csvString }) => {
      if (csvString) {
        DownloadCsv(csvString, 'Orders');
        setopenGenSnack(false);
      } else {
        setopenGenSnackError(true);
      }
    });
  };

  const popMenuComponents = [
    {
      name: t('AllOrdersTab.exportPdf'),
      icon: <InsertDriveFile />,
      disabledTooltip: t('AllOrdersTab.exportDisabledTooltipPDF'),
      disabled: totalWorkOrders > 250,
      onClick: exportPdf,
    },
    {
      name: t('AllOrdersTab.exportCSV'),
      icon: <FilePresent />,
      disabledTooltip: t('AllOrdersTab.exportDisabledTooltipCSV'),
      disabled: totalWorkOrders > 1000,
      onClick: exportCsv,
    },
    {
      name: t('AllOrdersTab.clear'),
      icon: <HighlightOff />,
      onClick: handleClear,
    },
  ];

  getPopMenu(view, popMenuComponents);

  const hideStateStyles = {
    filters: {
      display: 'none',
    },
  };

  const itemsByMediumScreen = {
    true: {
      flexWrap: 'nowrap',
      widthSearch: '20%',
    },
    false: {
      flexWrap: 'wrap',
      widthSearch: '100%',
    },
  };

  return (
    <>
      <GenSnackBar
        message={t('AllOrdersTab.exporting')}
        open={openGenSnack}
        icon={false}
        color={'#323232'}
        onClose={() => setopenGenSnack(false)}
      />

      <GenSnackBar
        message={t('AllOrdersTab.errorExporting')}
        open={openGenSnackError}
        icon={false}
        type="error"
        onClose={() => setopenGenSnackError(false)}
      />

      <Stack
        direction="column"
        flexWrap={itemsByMediumScreen[largeScreen].flexWrap}
        gap={1}
        width="100%"
      >
        <Stack
          direction="row"
          flexWrap="nowrap"
          gap={2}
          width={'100%'}
          justifyContent="space-between"
        >
          <Grid container justifyContent={'space-between'}>
            <Grid item xs={12} md={6}>
              <GenSearch
                placeholder={
                  <Trans
                    t={t}
                    i18nKey="orders:search"
                    components={{ 1: <br /> }}
                  />
                }
                searchTerm={filter => {
                  setSearchQuery(filter);
                }}
                defaultValue={currentFilters.searchQuery}
                sx={{
                  width: '100%',
                  '& form': { margin: '0px !important', height: '100%' },
                }}
              />
            </Grid>
            <Stack
              direction="row"
              gap={2}
              alignItems="center"
              justifyContent="space-between"
              sx={{ width: { xs: 'inherit', md: 'auto' } }}
              pt={{ xs: 2, md: 0 }}
              pb={{ xs: 1, md: 0 }}
            >
              {largeScreen &&
                showFilterWoCount(totalWorkOrders, isLoading, tab, true)}
              <Stack direction="row" gap={2}>
                <Badge
                  color="secondary"
                  variant="dot"
                  invisible={!activeFilters(currentFilters, isDoneTab)}
                >
                  <Button
                    variant="outlined"
                    sx={{
                      minWidth: 'auto',
                      color: theme.palette.text.primary,
                      borderColor: theme.palette.divider,
                      background: theme.palette.background.paper,
                      padding: '5px 7px',
                      textTransform: 'none',
                    }}
                    onClick={filterDisplayHandler}
                  >
                    {!smallScreen && (
                      <Typography sx={{ marginRight: 1 }} variant="body2">
                        Filters
                      </Typography>
                    )}
                    <Tune />
                  </Button>
                </Badge>
                {mediumScreenDown &&
                  showFilterWoCount(totalWorkOrders, isLoading, tab, false)}
              </Stack>
              {children}
            </Stack>
          </Grid>
        </Stack>
        {largeScreen && showDivider()}
        <Stack
          direction="row"
          flexWrap={largeScreen ? 'nowrap' : 'wrap'}
          gap={smallScreen ? 1 : 2}
          width={'100%'}
          sx={{
            ...(!filterDisplay && hideStateStyles.filters),
          }}
        >
          <MultiSelectFilter
            currentFilters={currentFilters}
            inputLabel={t('orders:newOrder.services.department')}
            itemFilter={'selectedDepartments'}
            menuItemGetter={department => (
              <MenuItem
                key={department.departmentId}
                value={department.departmentId}
              >
                {department.departmentName}
              </MenuItem>
            )}
            onFilter={onFilter}
            options={departments}
            sufix={'departments'}
            hide={hideFilterByDepartments}
          />

          <MultiSelectFilter
            currentFilters={currentFilters}
            inputLabel={t('orders:tableHeaders.vendorName')}
            itemFilter="selectedVendors"
            menuItemGetter={(vendor, index) => (
              <MenuItem key={vendor.companyId} value={vendor.companyId}>
                {vendor.rootCompany.name}
              </MenuItem>
            )}
            onFilter={onFilter}
            options={vendors}
            sufix={t('AllOrdersTab.vendorsChip')}
          />
          <MultiSelectFilter
            currentFilters={currentFilters}
            itemFilter="selectedSteps"
            menuItemGetter={(step, index) => (
              <MenuItem key={index} value={step}>
                {step}
              </MenuItem>
            )}
            onFilter={onFilter}
            options={VALID_STEPS}
            inputLabel={t('orders:tableHeaders.step')}
            sufix={t('AllOrdersTab.stepsChip')}
            hide={hideFilterBySteps}
          />
          <Stack
            width={largeScreen ? 'unset' : '100%'}
            flexGrow={dateRangeFlexGrow}
          >
            <DateRange
              sx={{
                width: '100%',
                paddingBottom: '0px',
                gap: { xs: 1, sm: 2 },
                ...(smallScreen && { flexWrap: 'wrap' }),
              }}
              startingDate={currentFilters.dateRange.startingDate}
              endDate={currentFilters.dateRange.endDate}
              setPeriod={setDateRangeWrapper}
              dateInUTC={true}
              errorHandler={handleClear}
              startPlaceholder={startPlaceholder}
              endPlaceholder={endPlaceholder}
            />
          </Stack>
          {largeScreen && (
            <GenContextualMenu
              options={popMenuComponents}
              buttonId="avatar-menu-button"
              menuId="mainMenuKebabMenu"
              type="outlined"
              ButtonText={<MoreVert />}
              size="small"
              sx={{
                padding: '7px',
                minWidth: 'auto',
                color: theme.palette.text.primary,
                borderColor: theme.palette.divider,
                background: theme.palette.background.paper,
                height: 'fit-content',
              }}
              anchorPosition={{
                vertical: 'bottom',
                horizontal: 'left',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'left',
              }}
            />
          )}
        </Stack>
        {!largeScreen && (
          <Stack
            width="100%"
            alignItems="flex-start"
            gap={1}
            sx={{
              ...(!largeScreen && !filterDisplay && hideStateStyles.filters),
            }}
          >
            <Button
              size="small"
              variant="text"
              startIcon={<HighlightOff />}
              sx={{ color: theme.palette.text.primary }}
              onClick={() => handleClear()}
            >
              {t('AllOrdersTab.clear')}
            </Button>
            <Divider orientation="horizontal" flexItem />
            <Box sx={{ marginTop: filterDisplay ? '0px' : '16px' }}>
              <FilterWoCount
                totalWorkOrders={totalWorkOrders}
                isLoading={isLoading}
                tab={tab}
              />
            </Box>
          </Stack>
        )}
      </Stack>
    </>
  );
};

export default Filters;
